import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@goaltree/ui/components/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@goaltree/ui/components/dropdown-menu'
import { cn } from '@goaltree/ui/lib/utils'
import { Check, ChevronRight, Flame, MoreHorizontal } from 'lucide-react'
import { type ReactNode, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useHoldToWater } from '@/components/hold-to-water'
import { ctaPeriod } from '@/components/inbox-habits'
import { useHabitCheckStyle } from '@/hooks/use-habit-check-style'
import { useCategoryFill } from '@/lib/category-color'

// Building blocks for the task/habit detail sheets: one primary action, label →
// value property rows, and a ⋯ menu for rare/destructive actions.

export function DetailPrimaryAction({
  kind,
  period,
  done,
  onClick,
  aside,
}: {
  kind: 'habit' | 'task'
  // Habit cadence, so a weekly habit reads "this week".
  period?: string | null
  done: boolean
  onClick: () => void
  // Shown above the button (habits: streak summary).
  aside?: ReactNode
}) {
  const { t } = useTranslation()
  // One button for tasks and habits: a filled call to action until done, then a
  // soft "Done · Undo" state so the undo is obvious.
  const unit = kind === 'habit' ? ctaPeriod(period) : 'Task'
  const label = t(`cta.${done ? 'done' : 'mark'}${unit}`)
  const Icon = kind === 'habit' ? Flame : Check
  const hold = useHabitCheckStyle() === 'hold' && kind === 'habit'
  const water = useHoldToWater({
    enabled: hold && !done,
    onComplete: onClick,
    tone: 'text-primary-foreground/30',
    rounded: 'rounded-[11px]',
  })
  return (
    <div className="flex flex-col gap-2">
      {aside}
      <button
        type="button"
        aria-pressed={done}
        title={hold && !done ? t('holdToWater.hint') : undefined}
        {...water.handlers}
        onClick={() => {
          if (!water.swallowClick()) onClick()
        }}
        className={cn(
          'relative flex w-full min-w-0 cursor-pointer items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-bold whitespace-nowrap transition-colors',
          hold && 'select-none [-webkit-touch-callout:none]',
          done
            ? 'border-primary/30 bg-primary/10 text-primary-text hover:bg-primary/15'
            : 'border-primary bg-primary text-primary-foreground hover:bg-primary/90',
        )}
      >
        {water.water}
        <Icon
          className={cn('relative h-4 w-4 shrink-0', kind === 'habit' && done && 'fill-current')}
          strokeWidth={kind === 'habit' ? 2.2 : 2.6}
        />
        <span className="relative truncate">{label}</span>
      </button>
    </div>
  )
}

export function DetailKindLabel({ children }: { children: ReactNode }) {
  return (
    <span className="min-w-0 flex-1 truncate text-xs font-semibold uppercase tracking-widest text-muted-foreground/60">
      {children}
    </span>
  )
}

export type DetailPathNode = { title: string; onClick?: () => void }

function PathLine({
  onClick,
  indent = 0,
  children,
}: {
  onClick?: () => void
  indent?: number
  children: ReactNode
}) {
  const Comp = onClick ? 'button' : 'div'
  return (
    <Comp
      {...(onClick ? { type: 'button' as const, onClick } : {})}
      style={indent ? { paddingLeft: 4 + (indent - 1) * 16 } : undefined}
      className={cn(
        'flex w-full items-start gap-2 rounded-md px-1 py-1 text-left text-sm',
        onClick && 'cursor-pointer transition-colors hover:bg-muted/50',
      )}
    >
      {children}
    </Comp>
  )
}

// Where the task lives: goal → milestone → target (missing levels already
// dropped). Loose tasks and habits belong to no goal, so nothing renders.
export function DetailPartOf({ color, path }: { color?: string; path: DetailPathNode[] }) {
  const { t } = useTranslation()
  const fill = useCategoryFill()
  const [goal, ...rest] = path
  if (!goal) return null
  return (
    <div className="flex flex-col gap-0.5">
      <span className="px-1 pb-0.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
        {t('inbox.partOf')}
      </span>
      <PathLine onClick={goal.onClick}>
        <span
          className="mt-[7px] h-2 w-2 shrink-0 rounded-full"
          style={{ background: color ? fill(color) : 'var(--muted-foreground)' }}
        />
        <span className="line-clamp-2 min-w-0 flex-1 font-medium text-foreground">
          {goal.title}
        </span>
        <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/60" />
      </PathLine>
      {rest.map((n, i) => (
        <PathLine key={i} onClick={n.onClick} indent={i + 1}>
          <span className="shrink-0 text-muted-foreground/40">└</span>
          <span className="line-clamp-2 min-w-0 flex-1 text-muted-foreground">{n.title}</span>
        </PathLine>
      ))}
    </div>
  )
}

export function DetailRows({ children }: { children: ReactNode }) {
  return <div className="border-t border-border/60">{children}</div>
}

export function DetailRow({
  label,
  children,
  onClick,
}: {
  label: string
  children: ReactNode
  // Whole row opens the value's picker; omit when the value is its own control.
  onClick?: () => void
}) {
  const Comp = onClick ? 'button' : 'div'
  return (
    <Comp
      {...(onClick ? { type: 'button' as const, onClick } : {})}
      className={cn(
        'grid w-full grid-cols-[96px_1fr] items-center gap-3 border-b border-border/60 px-1 py-2 text-left',
        onClick && 'cursor-pointer rounded-md transition-colors hover:bg-muted/50',
      )}
    >
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1 text-xs tabular-nums text-foreground">
        {children}
      </span>
    </Comp>
  )
}

export type DetailMenuItem = {
  key: string
  label: string
  icon: ReactNode
  onSelect: () => void
  destructive?: boolean
  // Shown as a labelled button in the sheet body (DetailActions) instead of inside the ⋯.
  inline?: boolean
  // Toggle that's currently on (e.g. pinned).
  active?: boolean
}

// ⋯ menu. A destructive item asks for confirmation before running.
export function DetailMenu({
  items,
  confirmTitle,
  confirmDescription,
}: {
  items: DetailMenuItem[]
  confirmTitle?: string
  confirmDescription?: string
}) {
  const { t } = useTranslation()
  const [confirming, setConfirming] = useState<DetailMenuItem | null>(null)
  const menu = items.filter((i) => !i.inline)
  const safe = menu.filter((i) => !i.destructive)
  const danger = menu.filter((i) => i.destructive)
  return (
    <div className="flex shrink-0 items-center gap-0.5">
      {menu.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger
            aria-label={t('common.more', { defaultValue: 'More' })}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <MoreHorizontal className="h-4 w-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-52">
            {safe.map((i) => (
              <DropdownMenuItem key={i.key} onClick={i.onSelect}>
                {i.icon}
                {i.label}
              </DropdownMenuItem>
            ))}
            {safe.length > 0 && danger.length > 0 && <DropdownMenuSeparator />}
            {danger.map((i) => (
              <DropdownMenuItem key={i.key} variant="destructive" onClick={() => setConfirming(i)}>
                {i.icon}
                {i.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <ConfirmDestructive
        item={confirming}
        onClose={() => setConfirming(null)}
        title={confirmTitle}
        description={confirmDescription}
      />
    </div>
  )
}

// Pin / delete etc. as labelled buttons below the details, so they're findable
// without opening the ⋯ menu. Destructive ones confirm first.
export function DetailActions({
  items,
  confirmTitle,
  confirmDescription,
}: {
  items: DetailMenuItem[]
  confirmTitle?: string
  confirmDescription?: string
}) {
  const [confirming, setConfirming] = useState<DetailMenuItem | null>(null)
  const inline = items.filter((i) => i.inline)
  const actions = inline.filter((i) => !i.destructive)
  // Labels stay on one line: when side-by-side buttons can't fit them (narrow
  // sheet, longer translations), each action takes its own row instead.
  const rowRef = useRef<HTMLDivElement>(null)
  const labelsKey = actions.map((i) => i.label).join('|')
  const [stackedFor, setStackedFor] = useState<string | null>(null)
  const stacked = stackedFor === labelsKey
  useLayoutEffect(() => {
    const row = rowRef.current
    if (stacked || !row) return
    const check = () => {
      if ([...row.children].some((b) => b.scrollWidth > b.clientWidth + 1)) setStackedFor(labelsKey)
    }
    check()
    const ro = new ResizeObserver(check)
    ro.observe(row)
    return () => ro.disconnect()
  }, [labelsKey, stacked])
  if (!inline.length) return null
  // Destructive actions get their own full-width row so the others keep room for
  // their labels in a narrow sheet.
  const danger = inline.filter((i) => i.destructive)
  const button = (i: DetailMenuItem) => (
    <button
      key={i.key}
      type="button"
      aria-pressed={i.active}
      onClick={() => (i.destructive ? setConfirming(i) : i.onSelect())}
      className={cn(
        'flex h-10 min-w-0 cursor-pointer items-center justify-center gap-2 overflow-hidden whitespace-nowrap rounded-xl px-3 text-sm font-semibold transition-colors active:scale-[0.98] [&_svg]:size-4 [&_svg]:shrink-0',
        i.destructive
          ? 'bg-destructive/10 text-destructive hover:bg-destructive/15'
          : i.active
            ? 'bg-primary/12 text-primary-text hover:bg-primary/18'
            : 'bg-muted/60 text-foreground hover:bg-muted',
      )}
    >
      {i.icon}
      {i.label.replace(/…$/, '')}
    </button>
  )
  return (
    <div className="grid shrink-0 gap-2">
      {actions.length > 0 && (
        <div
          ref={rowRef}
          className={cn(
            'grid gap-2',
            stacked
              ? 'grid-cols-1'
              : actions.length > 2
                ? 'grid-cols-2'
                : 'auto-cols-fr grid-flow-col',
          )}
        >
          {actions.map(button)}
        </div>
      )}
      {danger.map(button)}
      <ConfirmDestructive
        item={confirming}
        onClose={() => setConfirming(null)}
        title={confirmTitle}
        description={confirmDescription}
      />
    </div>
  )
}

function ConfirmDestructive({
  item,
  onClose,
  title,
  description,
}: {
  item: DetailMenuItem | null
  onClose: () => void
  title?: string
  description?: string
}) {
  const { t } = useTranslation()
  return (
    <AlertDialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title ?? item?.label}</AlertDialogTitle>
          {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            onClick={() => {
              item?.onSelect()
              onClose()
            }}
          >
            {/* The menu label's "…" signals a follow-up step; the confirm is that step. */}
            {item?.label.replace(/…$/, '')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
